import React from "react";
import {
  Avatar,
  Box,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { GridPaginationModel } from "@mui/x-data-grid";
import { Paginator } from "../../../components/ui/Paginator";
import { CRUD_VERBS, CrudVerb, VERB_LABEL, humanize } from "../roles/permissionUtils";
import { ApiApplicationGrant, ExternalResource } from "./types";
import { VisibleApp } from "./useApiApplicationGrantsPicker";
import { useThemeColors } from "../../../hooks/useThemeColors";

interface ApiApplicationGrantPickerProps {
  visibleApps: VisibleApp[];
  pagedApps: VisibleApp[];
  pagination: GridPaginationModel;
  setPagination: (p: GridPaginationModel) => void;
  grants: Record<string, ApiApplicationGrant>;
  expandedApps: Record<string, boolean>;
  toggleApp: (app: string) => void;
  toggleVerb: (appLabel: string, model: string, verb: CrudVerb) => void;
  resourceFor: (appLabel: string, model: string) => ExternalResource | undefined;
  onShowDocs: (resource: ExternalResource) => void;
  accentColor: string;
  maxHeight?: number | string;
}

const grantedVerbCount = (grant?: ApiApplicationGrant) =>
  grant ? CRUD_VERBS.filter((v) => grant[`can_${v}` as const]).length : 0;

const ApiApplicationGrantPicker: React.FC<ApiApplicationGrantPickerProps> = ({
  visibleApps,
  pagedApps,
  pagination,
  setPagination,
  grants,
  expandedApps,
  toggleApp,
  toggleVerb,
  resourceFor,
  onShowDocs,
  accentColor,
  maxHeight = 480,
}) => {
  const headingColor = useThemeColors().primary[100];
  if (visibleApps.length === 0) {
    return (
      <Paper
        sx={{
          boxShadow: 0,
          border: "1px dashed",
          borderColor: "divider",
          borderRadius: 3,
          p: 4,
          textAlign: "center",
        }}
      >
        <Typography color="text.secondary">
          No apps or models match the current search and filters.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Box display="flex" flexDirection="column" gap={1.5} sx={{ overflow: "auto", pr: 0.5, maxHeight }}>
        {pagedApps.map(([app, models]) => {
          const grantedModels = models.filter(
            (m) => grantedVerbCount(grants[`${app}.${m.model}`]) > 0,
          ).length;
          const apiModels = models.filter((m) => !!resourceFor(app, m.model)).length;
          const isExpanded = !!expandedApps[app];

          return (
            <Paper
              key={app}
              sx={{
                boxShadow: 0,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                overflow: "hidden",
                // Flex children of the scroll container shrink to fit maxHeight
                // otherwise (overflow hidden zeroes min-height), collapsing rows
                // into thin lines.
                flexShrink: 0,
                borderLeft: "4px solid",
                borderLeftColor: grantedModels > 0 ? accentColor : "divider",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                gap={1.5}
                px={2}
                py={1.25}
                sx={{ cursor: "pointer", "&:hover": { backgroundColor: "action.hover" } }}
                onClick={() => toggleApp(app)}
              >
                <Avatar
                  variant="rounded"
                  sx={{ width: 32, height: 32, bgcolor: "action.selected", color: "text.secondary" }}
                >
                  <LayersOutlinedIcon fontSize="small" />
                </Avatar>
                <Box flexGrow={1} minWidth={140}>
                  <Typography fontWeight="bold" noWrap sx={{ color: headingColor }}>
                    {humanize(app)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {models.length} model{models.length !== 1 ? "s" : ""}
                    {apiModels > 0 ? ` · ${apiModels} in the external API` : ""}
                  </Typography>
                </Box>
                <Chip
                  label={`${grantedModels} of ${models.length} granted`}
                  size="small"
                  color={grantedModels > 0 ? "primary" : "default"}
                  variant={grantedModels > 0 ? "filled" : "outlined"}
                />
                <IconButton size="small">
                  {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </IconButton>
              </Box>

              <Collapse in={isExpanded} timeout="auto" unmountOnExit mountOnEnter>
                <Divider />
                <Box px={2} py={1}>
                  {models.map((m) => {
                    const grant = grants[`${app}.${m.model}`];
                    const resource = resourceFor(app, m.model);
                    return (
                      <Box
                        key={m.model}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        flexWrap="wrap"
                        gap={1}
                        sx={{ py: 0.75, borderRadius: 2, "&:hover": { backgroundColor: "action.hover" }, px: 1 }}
                      >
                        <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 200 }}>
                          <Typography variant="body2" fontWeight={600} noWrap sx={{ color: headingColor }}>
                            {humanize(m.model)}
                          </Typography>
                          {resource && (
                            <>
                              <Chip label="API" size="small" variant="outlined" color="primary" />
                              <Tooltip title="API documentation for this table">
                                <IconButton size="small" onClick={() => onShowDocs(resource)}>
                                  <MenuBookOutlinedIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                        <Box display="flex" gap={0.5}>
                          {CRUD_VERBS.map((verb) => {
                            const writeOnReadOnly = !!resource && verb !== "view";
                            return (
                              <Tooltip
                                key={verb}
                                title={writeOnReadOnly ? "The external API is read-only in this release" : ""}
                              >
                                <FormControlLabel
                                  sx={{ mr: 0.5 }}
                                  control={
                                    <Checkbox
                                      size="small"
                                      color="primary"
                                      checked={!!grant?.[`can_${verb}` as const]}
                                      onChange={() => toggleVerb(app, m.model, verb)}
                                    />
                                  }
                                  label={
                                    <Typography
                                      variant="body2"
                                      color={writeOnReadOnly ? "text.disabled" : "text.secondary"}
                                    >
                                      {VERB_LABEL[verb]}
                                    </Typography>
                                  }
                                />
                              </Tooltip>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Collapse>
            </Paper>
          );
        })}
      </Box>
      <Paginator count={visibleApps.length} pagination={pagination} setPagination={setPagination} />
    </Box>
  );
};

export default React.memo(ApiApplicationGrantPicker);
